import React, { useMemo } from "react";
import { ArrowRight } from "lucide-react";
import { computeCoverage } from "../lib/coverage";

const SERVICES = [
  {
    name: "EU Representative",
    art: "GDPR Article 27",
    who: "Non-EU controllers or processors handling the personal data of EU residents.",
    entity: "Instant EU GDPR Representative Limited, Dublin",
    href: "https://gdprlocal.com/gdpr-article-27-eu-representative/",
    featured: true,
  },
  {
    name: "UK Representative",
    art: "UK GDPR Article 27",
    who: "Organisations outside the UK handling the personal data of UK residents.",
    entity: "GDPRLocal Ltd, Brighton",
    href: "https://gdprlocal.com/gdpr-article-27-uk-representative/",
  },
  {
    name: "Swiss Representative",
    art: "FADP Article 14",
    who: "Organisations outside Switzerland handling data of, or offering services to, Swiss residents.",
    entity: "\u00c9lite Med Group Sagl, Mendrisio",
    href: "https://gdprlocal.com/fadp-article-14-swiss-representative/",
  },
  {
    name: "EU AI Act Representative",
    art: "AI Act Article 22",
    who: "Non-EU providers placing certain AI systems on the EU market.",
    entity: "Instant EU GDPR Representative Limited, Dublin",
    href: "https://gdprlocal.com/ai-representative/",
    badge: "New",
  },
];

export const RepServices = ({ assumptions }) => {
  const needed = useMemo(() => {
    if (!assumptions) return {};
    const map = {};
    computeCoverage(assumptions).forEach((c) => (map[c.name] = c.status));
    return map;
  }, [assumptions]);
  const rank = { required: 0, recommended: 1, covered: 2, not_needed: 3, undefined: 4 };
  const ordered = [...SERVICES].sort((a, b) => (rank[needed[a.name]] ?? 4) - (rank[needed[b.name]] ?? 4));
  return (
  <section className="border-t hairline" data-testid="rep-services">
    <div className="max-w-[1000px] mx-auto px-5 lg:px-8 py-14">
      <div className="max-w-2xl">
        <span className="text-[12.5px] font-bold uppercase tracking-wide text-mutedink">Representative services</span>
        <h2 className="mt-2 text-3xl lg:text-4xl font-bold text-asphalt">One point of contact, wherever your users are</h2>
        <p className="mt-3 text-mutedink text-[15px] leading-relaxed">
          {assumptions ? "Ordered for your scenario. The services that apply to you are highlighted." : "If you handle the data of EU, UK or Swiss residents from outside those regions, the law often requires a local representative."}
        </p>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {ordered.map((s) => {
          const st = needed[s.name];
          const highlight = st === "required" || st === "recommended";
          return (
          <a
            key={s.name}
            href={s.href}
            className={`group relative rounded-2xl border-2 p-6 bg-white transition-all hover:-translate-y-1 ${highlight ? "border-aqua" : "hairline hover:border-asphalt/30"}`}
            data-testid={`rep-service-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {st === "required" && <span className="absolute -top-2.5 left-6 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "#FC5185", color: "#FFFFFF" }}>For you</span>}
            {st === "recommended" && <span className="absolute -top-2.5 left-6 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: "#2FE1EC", color: "#364F6B" }}>Recommended</span>}
            <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#3FC1C9" }}>{s.art}</div>
            <h3 className="mt-1 text-lg font-bold text-asphalt leading-tight">{s.name}</h3>
            <p className="mt-3 text-[13.5px] text-mutedink leading-relaxed">{s.who}</p>
            <div className="mt-4 text-[12px] text-mutedink">Delivered by {s.entity}</div>
            <div className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-medium text-asphalt group-hover:gap-2.5 transition-all">
              Learn more <ArrowRight size={15} />
            </div>
          </a>
          );
        })}
      </div>
    </div>
  </section>
  );
};
